import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption, IPSelectableOptionMenuItemType } from 'baseComponents/Utils/SelectableOption';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import {
  getPeriodFilterEndDate,
  getPeriodFilterStartDate,
  getPeriodURLParams,
} from 'components/Filter/PeriodFilterHelper';
import HrefLink from 'components/Links/HrefLink';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { TaskViewType } from 'components/Tasks/View/TasksView';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import Tag from 'models/tag';
import { TaskState } from 'models/tasks/taskHelperClasses';
import { FormEvent, FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTaskStatus } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { fromApiDateOptional } from 'utils/datetime';
import { getTaskStateColor } from 'utils/task';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetTaskStatusConfig {
  tagIds: number[];

  showTags: boolean;

  showAllTaskLink: boolean;

  tags: Tag[];

  useAndForTagGroups: boolean;

  taskViewType?: TaskViewType | undefined;

  period?: IPeriodFilterData;

  constructor() {
    this.tagIds = [];
    this.showTags = false;
    this.showAllTaskLink = false;
    this.tags = [];
    this.useAndForTagGroups = false;
    this.taskViewType = undefined;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
        this.showAllTaskLink = newRawConfig.showAllTaskLink ?? false;
        this.useAndForTagGroups = newRawConfig.useAndForTagGroups ?? false;
        this.taskViewType = newRawConfig.taskViewType;
        this.period = newRawConfig.period;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTaskStatusConfig {
    const newConfig = new WidgetTaskStatusConfig();
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;
    newConfig.showAllTaskLink = this.showAllTaskLink;
    newConfig.useAndForTagGroups = this.useAndForTagGroups;
    newConfig.taskViewType = this.taskViewType;
    newConfig.period = this.period;

    return newConfig;
  }
}

interface IWidgetTaskStatusProps extends IWidgetRendererProps {}

const WidgetTaskStatus: FunctionComponent<IWidgetTaskStatusProps> = (props: IWidgetTaskStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const projectId = props.projectId;
  const [data, setData] = useState<TaskState[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [taskCount, setTaskCount] = useState<number>(0);
  const [config, setConfig] = useState<WidgetTaskStatusConfig | undefined>(undefined);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();
      setConfig(config);

      //calculate start and end dates
      const customStart = fromApiDateOptional(config.period?.customStart);
      const customEnd = fromApiDateOptional(config.period?.customEnd);
      const start = getPeriodFilterStartDate(config.period?.option, config.period?.auditYear, customStart, appContext);
      const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const data = await apiGetWidgetDataTaskStatus(
        config.tags,
        config.useAndForTagGroups,
        start,
        end,
        accessToken,
        props.abortSignal,
        projectId,
      );

      setData(data.sort((a, b) => a.sortOrder - b.sortOrder));
      setTaskCount(data.reduce((sum, current) => sum + (current.taskCount ?? 0), 0));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConfig = (): WidgetTaskStatusConfig => {
    const newConfig = new WidgetTaskStatusConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const getData = (): IPieChartDataPoint[] => {
    if (data.length === 0) return [];

    const graphData: IPieChartDataPoint[] = data.map((s, idx) => {
      return {
        name: s.state,
        value: s.taskCount ?? 0,
        color: getTaskStateColor(s.taskStateId),
        link: getTasksUrl(s.taskStateId),
      };
    });

    return graphData;
  };

  const getTasksUrl = (taskStateId: number) => {
    if (!config) return undefined;

    let tagStr = '';
    if (config.tags.length > 0) {
      tagStr = `&tags=${config.tags.map((t) => t.tagId.toString()).join(',')}`;
      tagStr += `&useAndForTagGroups=${config.useAndForTagGroups}`;
    }

    let viewStr = '';
    if (config.taskViewType !== undefined) {
      viewStr += `&view=${config.taskViewType}`;
    }

    let periodStr = '';
    if (config.period) {
      periodStr += `&${getPeriodURLParams(config.period)}`;
    }

    const url = `/tasks/alltasks?status=${taskStateId.toString()}${tagStr}${viewStr}${periodStr}`;

    return url;
  };

  const getAllTasksUrl = () => {
    if (!config) return undefined;

    let tagStr = '';
    if (config.tags.length > 0) {
      tagStr = `?tags=${config.tags.map((t) => t.tagId.toString()).join(',')}`;
      tagStr += `&useAndForTagGroups=${config.useAndForTagGroups}`;
    } else {
      tagStr = `?clearFilter`;
    }

    let viewStr = '';
    if (config.taskViewType !== undefined) {
      viewStr = tagStr.length > 0 ? '&' : '?';
      viewStr += `view=${config.taskViewType}`;
    }

    let periodStr = '';
    if (config.period) {
      periodStr = viewStr.length > 0 || tagStr.length > 0 ? '&' : '?';
      periodStr += getPeriodURLParams(config.period);
    }

    const url = `/tasks/alltasks${tagStr}${viewStr}${periodStr}`;

    return url;
  };

  const getHeight = (): number => {
    if (config && config.tags.length > 0 && config.showTags && config.showAllTaskLink) {
      return 170;
    }

    if (config && ((config.tags.length > 0 && config.showTags) || config.showAllTaskLink)) {
      return 210;
    }

    return 250;
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (taskCount === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:TaskStatus.NoTasks')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <FluentUIPieChart
          data={getData()}
          height={getHeight()}
          width={280}
          label={taskCount.toString()}
        ></FluentUIPieChart>
      </IPStack.Item>
      {config && config.showAllTaskLink && (
        <HrefLink href={getAllTasksUrl()}>{t('widgets:TaskStatus.AllTaskLink')}</HrefLink>
      )}
    </IPStack>
  );
};

export default WidgetTaskStatus;

//
// Config
//

interface IWidgetConfigWidgetTaskStatusProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTaskStatus: FunctionComponent<IWidgetConfigWidgetTaskStatusProps> = (
  props: IWidgetConfigWidgetTaskStatusProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [showAllTasks, setShowAllTasks] = useState<boolean>(false);
  const [useAndForTagGroups, setUseAndForTagGroups] = useState<boolean>(false);
  const [taskViewType, setTaskViewType] = useState<TaskViewType | undefined>(undefined);
  const [period, setPeriod] = useState<IPeriodFilterData | undefined>(undefined);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTaskStatusConfig => {
    let newConfig = new WidgetTaskStatusConfig();
    if (props.widget.widgetConfig) {
      newConfig = JSON.parse(props.widget.widgetConfig);
      newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);
    }

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
      setShowAllTasks(config.showAllTaskLink);
      setUseAndForTagGroups(config.useAndForTagGroups);
      setTaskViewType(config.taskViewType);
      setPeriod(config.period);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    tags: Tag[],
    showTags: boolean,
    showAllTasks: boolean,
    useAndForTagGroups: boolean,
    taskViewType: TaskViewType | undefined,
    period: IPeriodFilterData | undefined,
  ): WidgetTaskStatusConfig => {
    const config = new WidgetTaskStatusConfig();
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;
    config.showAllTaskLink = showAllTasks;
    config.useAndForTagGroups = useAndForTagGroups;
    config.taskViewType = taskViewType;
    config.period = period;

    return config;
  };

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags, showAllTasks, useAndForTagGroups, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags, showAllTasks, useAndForTagGroups, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedTags, checked, showAllTasks, useAndForTagGroups, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetUseAndForTagGroups = (checked: boolean) => {
    setUseAndForTagGroups(checked);
    const config = getConfig(selectedTags, showTags, showAllTasks, checked, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowAllTasks = (checked: boolean) => {
    setShowAllTasks(checked);
    const config = getConfig(selectedTags, showTags, checked, useAndForTagGroups, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetTaskViewType = (taskViewType: TaskViewType | undefined) => {
    setTaskViewType(taskViewType);
    const config = getConfig(selectedTags, showTags, showAllTasks, useAndForTagGroups, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    setPeriod(period);
    const config = getConfig(selectedTags, showTags, showAllTasks, useAndForTagGroups, taskViewType, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const getTaskViewTypeOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [
      {
        key: 'notset',
        text: t('tasks:TaskView.Commands.View.NotSet'),
      },
      {
        key: 'div1',
        text: '-',
        itemType: IPSelectableOptionMenuItemType.Divider,
      },
      { key: TaskViewType.List, text: t('tasks:TaskView.Commands.View.List') },
      { key: TaskViewType.Board, text: t('tasks:TaskView.Commands.View.Board') },
      { key: TaskViewType.Schedule, text: t('tasks:TaskView.Commands.View.Schedule') },
      { key: TaskViewType.Timeline, text: t('tasks:TaskView.Commands.View.Timeline') },
    ];

    return options;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const taskViewTypeOptions = getTaskViewTypeOptions();

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <PeriodFilter
          showOption={true}
          showCurrentAuditYear={true}
          storageKey={undefined}
          onUpdatePeriodOption={onSetPeriod}
          period={period}
          showAll={true}
          disabled={props.widget.dashboard?.isContextType()}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:TaskStatus.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
        />
      </IPStack.Item>
      <IPCheckbox
        label={t('tasks:Filter.TagGroupAnd')}
        checked={useAndForTagGroups}
        onChange={(ev, checked) => {
          onSetUseAndForTagGroups(checked ?? false);
        }}
      />
      <IPStack.Item>
        <IPCheckbox
          checked={showTags}
          label={t('widgets:TaskStatus.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showAllTasks}
          label={t('widgets:TaskStatus.Config.LabelShowAllTasks')}
          onChange={(ev, checked) => {
            onSetShowAllTasks(checked ?? false);
          }}
        />
        {showAllTasks && (
          <IPStack styles={{ root: { width: 300, paddingLeft: 30, paddingTop: 10 } }}>
            <IPDropdown
              selectedKey={taskViewType ?? 'notset'}
              options={taskViewTypeOptions}
              onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                onSetTaskViewType(option?.key === 'notset' ? undefined : (option?.key as TaskViewType));
              }}
              prefix={t('tasks:TaskView.Commands.View.Main') + ' : '}
            />
          </IPStack>
        )}
      </IPStack.Item>
    </IPStack>
  );
};
